import React, { useEffect, useRef, useState } from "react";
// Import the ESM build of pdf.js
import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs";

// Create (and reuse) a module worker from our own domain
let sharedWorker;
function getPdfWorker() {
  if (!sharedWorker) {
    sharedWorker = new Worker("/pdf.worker.min.mjs", { type: "module" });
  }
  return sharedWorker;
}

/**
 * Canvas PDF viewer: no download/print UI, selection disabled, right-click blocked.
 * Allows going to a specific page. Fits to container width.
 */
export default function PdfViewer({ src, height = "80vh", onPagesLoaded }) {
  const shellRef = useRef(null);
  const canvasRef = useRef(null);
  const [doc, setDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [err, setErr] = useState("");

  // Block common save/print shortcuts
  const onKeyDown = (e) => {
    const k = e.key.toLowerCase();
    if ((e.ctrlKey || e.metaKey) && (k === "s" || k === "p" || k === "o")) e.preventDefault();
  };

  // Point pdf.js at our module worker
  useEffect(() => {
    pdfjsLib.GlobalWorkerOptions.workerPort = getPdfWorker();
  }, []);

  // Load the PDF
  useEffect(() => {
    let cancelled = false;
    setErr("");
    setDoc(null);
    setNumPages(0);
    setPage(1);

    const task = pdfjsLib.getDocument({
      url: src,
      rangeChunkSize: 65536,
      disableAutoFetch: false,
      disableStream: false,
    });

    task.promise
      .then((pdf) => {
        if (cancelled) return;
        setDoc(pdf);
        setNumPages(pdf.numPages);
        if (onPagesLoaded) onPagesLoaded(pdf.numPages);
      })
      .catch((e) => !cancelled && setErr(e?.message || String(e)));

    return () => {
      cancelled = true;
      try { task.destroy(); } catch {}
    };
  }, [src, onPagesLoaded]);

  // Re-render only when the container's WIDTH actually changes.
  // (Watching height too caused a feedback loop: rendering a new page
  // changed the canvas height, which fired ResizeObserver, which forced
  // another re-render, ratcheting the viewer larger on every page change.)
  useEffect(() => {
    if (!shellRef.current) return;
    let lastWidth = shellRef.current.clientWidth;
    const ro = new ResizeObserver(() => {
      const w = shellRef.current?.clientWidth ?? 0;
      if (w !== lastWidth) {
        lastWidth = w;
        setPage((p) => p); // force a re-render at the new width
      }
    });
    ro.observe(shellRef.current);
    return () => {
      try { ro.disconnect(); } catch {}
    };
  }, []);

  // Render current page into canvas, fit to container width.
  // Renders at devicePixelRatio for crisp output on hi-DPI screens.
  useEffect(() => {
    if (!doc || !canvasRef.current || !shellRef.current) return;
    let cancelled = false;
    let activeRenderTask = null;

    (async () => {
      try {
        const pdfPage = await doc.getPage(page);
        if (cancelled) return;

        const dpr = window.devicePixelRatio || 1;
        // Subtract the inner padding (p-4 = 16px each side = 32px) so the
        // canvas fits cleanly inside the shell without forcing horizontal
        // scroll — and so the measured width is consistent across renders.
        const SHELL_PADDING_X = 32;
        const containerWidth = Math.max(
          200,
          (shellRef.current.clientWidth || 800) - SHELL_PADDING_X
        );
        const viewport1 = pdfPage.getViewport({ scale: 1 });
        const cssScale = containerWidth / viewport1.width;
        const cssViewport = pdfPage.getViewport({ scale: cssScale });
        const renderViewport = pdfPage.getViewport({ scale: cssScale * dpr });

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d", { alpha: false });

        // Backing store at device pixels for sharpness
        canvas.width = Math.floor(renderViewport.width);
        canvas.height = Math.floor(renderViewport.height);
        // Display size in CSS pixels
        canvas.style.width = `${Math.floor(cssViewport.width)}px`;
        canvas.style.height = `${Math.floor(cssViewport.height)}px`;

        activeRenderTask = pdfPage.render({
          canvasContext: ctx,
          viewport: renderViewport,
          intent: "display",
        });
        await activeRenderTask.promise;
      } catch (e) {
        // Ignore cancellation errors that happen when navigating quickly
        if (!cancelled && e?.name !== "RenderingCancelledException") {
          setErr(e?.message || String(e));
        }
      }
    })();

    return () => {
      cancelled = true;
      try { activeRenderTask && activeRenderTask.cancel(); } catch {}
    };
  }, [doc, page]);

  if (err) return <div className="p-4 text-red-600">Failed to load PDF: {err}</div>;

  return (
    <div
      ref={shellRef}
      className="pdf-shell no-print select-none"
      style={{
        height,
        borderRadius: 8,
        border: "1px solid #e5e7eb",
        background: "#fff",
        overflow: "auto",
        // Reserve space for the scrollbar so clientWidth doesn't flip
        // between "with scrollbar" and "without scrollbar" on every page
        // change — that flip was making the canvas (and thus the white
        // container) ratchet larger on every click.
        scrollbarGutter: "stable",
        position: "relative",
        boxSizing: "border-box",
      }}
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={onKeyDown}
      tabIndex={0}
    >
      {/* Minimal pager */}
      <div
        className="absolute top-2 right-2 z-10"
        style={{
          background: "rgba(255,255,255,0.92)",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          padding: "6px 8px",
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          gap: 6,
          userSelect: "none",
        }}
        onContextMenu={(e) => e.preventDefault()}
      >
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          style={{ padding: "2px 6px", borderRadius: 6, border: "1px solid #ddd", background: "#f9fafb" }}
        >
          ◀
        </button>
        <span>Page</span>
        <input
          type="number"
          min={1}
          max={numPages || 1}
          value={page}
          onChange={(e) => {
            const v = Math.min(Math.max(1, Number(e.target.value || 1)), numPages || 1);
            setPage(v);
          }}
          style={{ width: 60, border: "1px solid #ddd", borderRadius: 6, padding: "2px 6px" }}
          onContextMenu={(e) => e.preventDefault()}
        />
        <span>/ {numPages || "…"}</span>
        <button
          onClick={() => setPage((p) => Math.min(numPages || p, p + 1))}
          disabled={!numPages || page >= numPages}
          style={{ padding: "2px 6px", borderRadius: 6, border: "1px solid #ddd", background: "#f9fafb" }}
        >
          ▶
        </button>
      </div>

      <div className="flex items-start justify-center p-4" style={{ userSelect: "none" }}>
        <canvas ref={canvasRef} style={{ display: "block" }} />
      </div>
    </div>
  );
}
